import { AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common'
import { Router } from '@angular/router';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { DutyPeriodSummaries } from '@shared/models/activity-summary/duty-period-summaries';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { ButtonModule } from 'primeng/button';
import { DataBoxComponent } from '@shared/components/data-box/data-box.component';
import {
  Map,
  LatLngExpression,
  LatLng,
} from 'leaflet'
import * as lfGeo from 'leaflet.geodesic';
import { DataItem } from '@shared/models/data-item';
import { SequenceTotalsInfoComponent } from './sequence-total-info/sequence-total-info.component'
import { formatTime, timeStringToDuration } from '@shared/utilities/utilities';
import { FlightItemComponent } from 'app/features/flights/flight-item/flight-item.component';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { MapService } from '../services/map.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { AppService } from 'app/app.service';
import { AppSettings } from '@shared/models/settings/app-settings';
import { Subscription } from 'rxjs';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ADD_CODES } from '@shared/utilities/add-codes';


@Component({
  selector: 'app-sequence-detail',
  standalone: true,
  imports: [
    CommonModule,
    LeafletModule,
    ButtonModule,
    DataBoxComponent,
    FlightItemComponent,
    BreadcrumbComponent,
    OverlayPanelModule
  ],
  templateUrl: './sequence-detail.component.html',
  styleUrl: './sequence-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SequenceDetailComponent implements OnInit, AfterViewChecked, OnDestroy {

  constructor(
    public appService: AppService,
    private router: Router,
    private mapService: MapService,
    private cdr: ChangeDetectorRef
  ) { }

  reasonCodes = ADD_CODES;
  private map?: Map;
  private coords?: LatLngExpression[][];
  private geoLine?: lfGeo.GeodesicLine;

  scheduledTime = '';
  actualTiime = '';
  deadheadTime = '';
  totalTime = '';

  sequence?: Sequences;
  sequenceTotalsInfoDialog = SequenceTotalsInfoComponent;
  sequenceTotalsDatabox: DataItem[] = [];
  tableListType = 'sequence';
  appSettings?: AppSettings;

  theme$?: string;
  sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.appService.theme$.subscribe( (theme) =>{
      this.theme$ = theme;
      this.cdr.markForCheck();
  });

    this.appSettings = JSON.parse(localStorage.getItem('appSettings') || '');
    const resolveSequence = sessionStorage.getItem('sequence');
    const nonfilteredSequence = sessionStorage.getItem('nonFilteredSequence');

    /**Full Sequence loaded from Search-Results component */
    if (nonfilteredSequence) {
      const sequence = JSON.parse(nonfilteredSequence);
      this.sequence = this.sortList(sequence[0]);
      this.loadDataBox(sequence[0]);
    }
    /** Sequence loaded from Flight Table list or calendar */
    else if (resolveSequence && nonfilteredSequence === null) {
      const sequence = JSON.parse(resolveSequence);
      this.sequence = sequence;
      this.loadDataBox(sequence);
    }

    const coords = this.getMapCoords(this.sequence?.dutyPeriodSummaries || []);
    this.map = this.mapService.renderMap(1, [], coords);
  }

  sortList(sequence: Sequences): Sequences {
    sequence;
    
    if (this.appSettings?.sortOrder === 1) {
      sequence.dutyPeriodSummaries = sequence.dutyPeriodSummaries.reverse();
    }
    return sequence;
  }

  ngAfterViewChecked(): void {
    this.map = this.mapService.initMap();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  loadDataBox(sequence: Sequences): void {
    this.sequenceTotalsDatabox =
      [
        {
          label: 'Deadhead',
          value: this.flightTotalizerFor('deadhead', sequence)
        },
        {
          label: 'Flight',
          value: this.flightTotalizerFor('flight', sequence)
        },
        {
          label: 'Total',
          value: this.flightTotalizerFor('total', sequence)
        },
        {
          label: 'TAFB',
          value: timeStringToDuration(String(sequence.duration)).toFormat('hh:mm')
        },
      ];
  }

  onResetMap(): void {
    this.mapService.initMap();
  }

  flightTotalizerFor(_timeFor: string, sequence: Sequences): string {
    let time = 0;

    switch (_timeFor) {
      case 'deadhead':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && leg.isDeadhead && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);
      case 'flight':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && !leg.isDeadhead && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);

      case 'total':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);

      default:
        return ''
    }
  }

  getMapCoords(dutyPeriods: DutyPeriodSummaries[]): LatLngExpression[][] {
    let coords: LatLngExpression[][] = [];
    for (let duty of dutyPeriods) {
      duty.legs.forEach((leg) => {
        if (leg) {
          const stationLatlng = [

            new LatLng(leg.departureStation.latitude, leg.departureStation.longitude),
            new LatLng(leg.arrivalStation.latitude, leg.arrivalStation.longitude)

          ];
          coords.push(stationLatlng);
        }
      })
    }
    return coords
  }


  onLegClicked(selectedLeg: LogbookLeg, sequence: Sequences, to: string): void {
    const leg = JSON.stringify(selectedLeg);
    const selectedSequence = JSON.stringify(sequence);

    const _to = JSON.parse(sessionStorage.getItem('to') || '');
    sessionStorage.setItem('leg', leg);
    sessionStorage.setItem('sequence', selectedSequence);

    if (_to === 'search-result-seq') {
      sessionStorage.setItem('to', JSON.stringify('search-result-seq-leg'));
      this.router.navigate(['flight-detail']);
    }
    else if (_to === 'cal-sequence-detail') {
      sessionStorage.setItem('to', JSON.stringify('cal-flight-detail'));
      this.router.navigate(['flight-detail']);
    }
    else {
      sessionStorage.setItem('to', JSON.stringify('sequence-flight-detail'));
      this.router.navigate(['flight-detail'])
    }
  }

  validateReasonCode(reasonCode: string): string {
    const reasonCodeTrimmed = reasonCode.trim();
    if (reasonCodeTrimmed === '') {
      return '??';
    }
    return reasonCodeTrimmed;
  }

}
